import { useEffect, useState } from "react";
import { Check, X, Sparkles, MessageSquare, PartyPopper, Flag, Ban } from "lucide-react";
import { toast } from "sonner";
import { KidAvatar } from "../Brand";
import { parentApi, streamUrl, thumbUrl, errMsg, PARENT_KEY } from "../../lib/api";
import { catById, timeAgo } from "../../lib/constants";

const AiNote = ({ check }) => (
  <span className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#E6FBF0] border-2 border-[#1B1A2E]">
    <Sparkles size={12} /> {check?.ai ? "AI: looks safe" : check?.reason || "AI check unavailable"}
  </span>
);

const VideoReview = ({ v, onDone }) => {
  const [note, setNote] = useState("");
  const review = async (action) => {
    try {
      await parentApi.post(`/parent/videos/${v.id}/review`, { action, note });
      toast.success(action === "approve" ? "Video is live!" : "Sent back with your note");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`pending-video-${v.id}`} className="zn-card p-5 bg-white grid grid-cols-1 md:grid-cols-[320px_1fr] gap-5">
      <video data-testid={`pending-video-player-${v.id}`} src={streamUrl(v.id, localStorage.getItem(PARENT_KEY))} poster={v.thumb ? thumbUrl(v.id, localStorage.getItem(PARENT_KEY)) : undefined} controls playsInline preload="metadata" className="w-full max-h-[60svh] md:aspect-video rounded-2xl border-[3px] border-[#1B1A2E] bg-black object-contain" />
      <div className="space-y-3 min-w-0">
        <div className="flex items-center gap-2"><KidAvatar avatar={v.author?.avatar} color={v.author?.color} size={32} /><b>{v.author?.name}</b><span className="text-sm text-[#1B1A2E]/60">· {timeAgo(v.created_at)}</span></div>
        <h3 className="font-display text-2xl font-semibold break-words">{v.title}</h3>
        {v.description && <p className="text-sm text-[#1B1A2E]/80">{v.description}</p>}
        <div className="flex flex-wrap gap-2"><span className="text-xs px-2 py-1 rounded-full border-2 border-[#1B1A2E]" style={{ background: catById(v.category).color }}>{catById(v.category).label}</span><AiNote check={v.ai_check} />{v.ai_visual_check && <span data-testid={`ai-visual-${v.id}`} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#E6FBF0] border-2 border-[#1B1A2E]"><Sparkles size={12} /> {v.ai_visual_check.ai ? `AI video scan: safe (${v.ai_visual_check.frames} frames)` : v.ai_visual_check.reason}</span>}{v.duration && <span className="text-xs px-2 py-1 rounded-full border-2 border-[#1B1A2E]">{v.duration}s</span>}</div>
        <input data-testid={`review-note-input-${v.id}`} className="zn-input !py-2 text-sm" placeholder="Optional note for your kid (shown if rejected)" value={note} onChange={(e) => setNote(e.target.value)} />
        <div className="flex gap-3">
          <button data-testid={`approve-video-${v.id}`} onClick={() => review("approve")} className="zn-btn bg-[#B8F2C9] !py-2 flex-1 sm:flex-none"><Check size={18} /> Approve</button>
          <button data-testid={`reject-video-${v.id}`} onClick={() => review("reject")} className="zn-btn bg-white !py-2 flex-1 sm:flex-none"><X size={18} /> Reject</button>
        </div>
      </div>
    </div>
  );
};

const CommentReview = ({ c, onDone }) => {
  const review = async (action) => {
    try {
      await parentApi.post(`/parent/comments/${c.id}/review`, { action });
      toast.success(action === "approve" ? "Comment approved" : "Comment rejected");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`pending-comment-${c.id}`} className="zn-card p-4 bg-white flex flex-col sm:flex-row gap-4 sm:items-center">
      <KidAvatar avatar={c.author?.avatar} color={c.author?.color} size={40} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-[#1B1A2E]/60"><b className="text-[#1B1A2E]">{c.author?.name}</b> on "{c.video_title}" · {timeAgo(c.created_at)}</p>
        <p className="text-lg break-words">"{c.text}"</p>
        <AiNote check={c.ai_check} />
      </div>
      <div className="flex gap-2">
        <button data-testid={`approve-comment-${c.id}`} onClick={() => review("approve")} className="zn-btn bg-[#B8F2C9] !py-2 !px-4 flex-1 sm:flex-none" aria-label="Approve comment"><Check size={18} /><span className="sm:hidden">Approve</span></button>
        <button data-testid={`reject-comment-${c.id}`} onClick={() => review("reject")} className="zn-btn bg-white !py-2 !px-4 flex-1 sm:flex-none" aria-label="Reject comment"><X size={18} /><span className="sm:hidden">Reject</span></button>
      </div>
    </div>
  );
};

const ReportReview = ({ r, onDone }) => {
  const resolve = async (action) => {
    try {
      await parentApi.post(`/parent/reports/${r.id}/resolve`, { action });
      toast.success(action === "block_creator" ? `${r.target_creator?.name || "Creator"} is now blocked for ${r.reporter?.name}` : "Report dismissed");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`open-report-${r.id}`} className="zn-card p-4 bg-white flex flex-col sm:flex-row gap-4 sm:items-center">
      <span className="w-10 h-10 shrink-0 grid place-items-center rounded-xl border-[3px] border-[#1B1A2E] bg-[#FF8FC7]"><Flag size={18} /></span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-[#1B1A2E]/60"><b className="text-[#1B1A2E]">{r.reporter?.name}</b> reported · {timeAgo(r.created_at)}</p>
        <p className="font-semibold break-words">{r.target_label}</p>
        <p className="text-sm">Reason: {r.reason.replace("_", " ")}{r.target_creator ? ` · by ${r.target_creator.name}` : ""}</p>
      </div>
      <div className="flex gap-2">
        {r.target_kid_id !== r.reporter_kid_id && <button data-testid={`block-report-${r.id}`} onClick={() => resolve("block_creator")} className="zn-btn bg-[#FFD23F] !py-2 !px-4 text-sm"><Ban size={16} /> Block creator</button>}
        <button data-testid={`dismiss-report-${r.id}`} onClick={() => resolve("dismiss")} className="zn-btn bg-white !py-2 !px-4 text-sm">Dismiss</button>
      </div>
    </div>
  );
};

export const ApprovalQueue = ({ onChange }) => {
  const [q, setQ] = useState(null);
  const load = () => parentApi.get("/parent/queue").then((r) => setQ(r.data));
  useEffect(() => { load(); }, []);
  const done = () => { load(); onChange(); };

  if (!q) return <p className="font-display text-xl">Loading…</p>;
  const empty = !q.videos.length && !q.comments.length && !q.reports.length;
  return (
    <div className="space-y-8">
      <h1 className="text-4xl font-bold">Approval queue</h1>
      {empty && (
        <div data-testid="queue-empty" className="zn-card p-10 text-center bg-[#B8F2C9]">
          <PartyPopper size={40} className="mx-auto mb-3" />
          <p className="font-display text-2xl font-semibold">All caught up!</p>
          <p>Nothing waiting for review right now.</p>
        </div>
      )}
      {q.videos.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Videos ({q.videos.length})</h2>
          {q.videos.map((v) => <VideoReview key={v.id} v={v} onDone={done} />)}
        </div>
      )}
      {q.comments.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold flex items-center gap-2"><MessageSquare size={22} /> Comments ({q.comments.length})</h2>
          {q.comments.map((c) => <CommentReview key={c.id} c={c} onDone={done} />)}
        </div>
      )}
      {q.reports.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold flex items-center gap-2"><Flag size={22} /> Reports from your kids ({q.reports.length})</h2>
          {q.reports.map((r) => <ReportReview key={r.id} r={r} onDone={done} />)}
        </div>
      )}
    </div>
  );
};
